import { LANGUAGES } from '../../_i18n/languages';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', locale: 'en-US', label: 'English', flag: '🇺🇸' },
  { code: 'pt', locale: 'pt-BR', label: 'Português', flag: '🇧🇷' },
  { code: 'es', locale: 'es-ES', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', locale: 'fr-FR', label: 'Français', flag: '🇫🇷' },
  { code: 'it', locale: 'it-IT', label: 'Italiano', flag: '🇮🇹' },
  { code: 'ru', locale: 'ru-RU', label: 'Русский', flag: '🇷🇺' },
  { code: 'de', locale: 'de-DE', label: 'Deutsch', flag: '🇩🇪' },
];

/**
 * Extracts the current active language code ('en', 'pt', etc.) from pathname or intl
 */
export function getCurrentLang(pathname = '', intl = null) {
  const cleanPath = (pathname || '').split('?')[0].split('#')[0];
  const parts = cleanPath.split('/').filter(Boolean);

  // e.g. /pt or /es (home page in language)
  if (parts.length === 1 && LANGUAGES[parts[0]] && parts[0] !== 'default') {
    return parts[0];
  }

  // e.g. /replace-text/pt or /calculator/es (tool in language)
  if (parts.length >= 2 && LANGUAGES[parts[1]] && parts[1] !== 'default') {
    return parts[1];
  }

  // Check any path segment for a recognized language
  for (let p of parts) {
    if (LANGUAGES[p] && p !== 'default') {
      return p;
    }
  }

  // Fallback to intl locale if available
  if (intl && intl.locale) {
    const matched = Object.keys(LANGUAGES).find(
      k => k !== 'default' && LANGUAGES[k].code === intl.locale
    );
    if (matched) return matched;
  }

  return 'en';
}

/**
 * Generates an SEO-friendly localized URL preserving the user's active language.
 * Default English stays at root /:tool without /en suffix (clean canonical URL).
 * Other languages get /:tool/:lang or /:lang for home.
 */
export function getLocalizedUrl(basePath, lang = 'en') {
  if (!basePath) return lang === 'en' ? '/' : `/${lang}`;

  // Clean trailing and leading slashes for normalization
  const trimmed = basePath.replace(/^\/+/, '').replace(/\/+$/, '');

  // Home page handling
  if (!trimmed) {
    return lang === 'en' ? '/' : `/${lang}`;
  }

  // Remove existing language suffix if present in basePath
  const segments = trimmed.split('/');
  let toolName = segments[0];
  if (segments.length >= 2 && LANGUAGES[segments[1]]) {
    toolName = segments[0];
  } else if (segments.length === 1 && LANGUAGES[segments[0]]) {
    toolName = '';
  }

  if (!toolName) {
    return lang === 'en' ? '/' : `/${lang}`;
  }

  if (lang === 'en' || !lang) {
    return `/${toolName}`;
  }

  return `/${toolName}/${lang}`;
}

/**
 * Generates the target URL for switching the current page to another language.
 */
export function getSwitchLangUrl(targetLang, currentPathname = '/') {
  const cleanPath = (currentPathname || '/').replace(/^https?:\/\/[^/]+/, '').split('?')[0].split('#')[0];
  const parts = cleanPath.split('/').filter(Boolean);

  // Home in English '/' or home in another language '/pt'
  if (parts.length === 0 || (parts.length === 1 && LANGUAGES[parts[0]])) {
    return targetLang === 'en' ? '/' : `/${targetLang}`;
  }

  // Tool page: get tool name
  const toolName = parts[0];
  if (targetLang === 'en') {
    return `/${toolName}`;
  }

  return `/${toolName}/${targetLang}`;
}

/**
 * Generates hreflang alternate URL list for a given tool/base path.
 */
export function getHreflangList(basePath, domain = 'https://textcaseconvert.com') {
  const cleanBase = (basePath || '').replace(/^https?:\/\/[^/]+/, '').replace(/^\/+/, '').replace(/\/+$/, '');
  const segments = cleanBase.split('/');
  const toolName = segments.length > 0 && !LANGUAGES[segments[0]] ? segments[0] : '';
  const root = toolName ? `${domain}/${toolName}` : domain;

  return [
    { hreflang: 'x-default', href: root },
    { hreflang: 'en', href: root },
    { hreflang: 'pt', href: toolName ? `${domain}/${toolName}/pt` : `${domain}/pt` },
    { hreflang: 'es', href: toolName ? `${domain}/${toolName}/es` : `${domain}/es` },
    { hreflang: 'fr', href: toolName ? `${domain}/${toolName}/fr` : `${domain}/fr` },
    { hreflang: 'it', href: toolName ? `${domain}/${toolName}/it` : `${domain}/it` },
    { hreflang: 'ru', href: toolName ? `${domain}/${toolName}/ru` : `${domain}/ru` },
    { hreflang: 'de', href: toolName ? `${domain}/${toolName}/de` : `${domain}/de` },
  ];
}
